var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { EventEmitter } from 'events';
import { ActionTypes, GamesAccountStatuses } from 'constants/AdminConstants';
import { changeGameAccountStatusActionSchema, deleteGameActionSchema, fetchGameActionSchema, updateGameTrackableSchema } from 'types/actions/gameStateActions';
import { replaceElementAtIndex } from 'utils/FunctionalUtils';
import { GameStoreStateSchema } from './types';
import Dispatcher from '../../dispatcher/Dispatcher';
var CHANGE_EVENT = 'change';
var GamesStore = /** @class */ (function (_super) {
    __extends(GamesStore, _super);
    function GamesStore() {
        var _this = _super.call(this) || this;
        _this.data = {
            games: {}
        };
        _this.registerDispatcher();
        return _this;
    }
    GamesStore.prototype.registerDispatcher = function () {
        var _this = this;
        Dispatcher.register(function (action) {
            switch (action.type) {
                case ActionTypes.FETCH_GAME:
                    _this.handleFetchGameAction(action);
                    break;
                case ActionTypes.DELETE_GAME:
                    _this.handleDeleteGameAction(action);
                    break;
                case ActionTypes.CHANGE_GAME_ACCOUNT_STATUS:
                    _this.handleGameAccountStatusAction(action);
                    break;
                case ActionTypes.UPDATE_GAME_TRACKABLE_MAPPING:
                    _this.handleGameTrackableMappingUpdate(action);
                    break;
                default:
                    // No action needed for unhandled action types
                    break;
            }
        });
    };
    GamesStore.prototype.init = function (initData) {
        var parsedData = GameStoreStateSchema.safeParse(initData);
        if (parsedData.success) {
            this.data = parsedData.data;
        }
        else {
            console.error('Error parsing game store state', parsedData.error);
        }
    };
    GamesStore.prototype.addChangeListener = function (callback) {
        this.on(CHANGE_EVENT, callback);
    };
    GamesStore.prototype.removeChangeListener = function (callback) {
        this.removeListener(CHANGE_EVENT, callback);
    };
    GamesStore.prototype.hasGame = function (id) {
        return this.data.games[id] !== undefined;
    };
    GamesStore.prototype.getGame = function (id) {
        var game = this.data.games[id];
        return game || false;
    };
    GamesStore.prototype.setGame = function (id, game) {
        this.data.games[id] = game;
        this.emit(CHANGE_EVENT);
    };
    GamesStore.prototype.deleteGame = function (id) {
        delete this.data.games[id];
        this.emit(CHANGE_EVENT);
    };
    GamesStore.prototype.handleFetchGameAction = function (action) {
        var parsingResult = fetchGameActionSchema.safeParse(action);
        if (!parsingResult.success) {
            console.error('Error parsing fetch game action', parsingResult.error);
            return;
        }
        var game = parsingResult.data.data;
        this.setGame(game.settings.id, game);
    };
    GamesStore.prototype.handleDeleteGameAction = function (action) {
        var parsingResult = deleteGameActionSchema.safeParse(action);
        if (!parsingResult.success) {
            console.error('Error parsing delete game action', parsingResult.error);
            return;
        }
        var deleteData = parsingResult.data.data;
        var gameId = deleteData.id;
        this.deleteGame(gameId);
    };
    GamesStore.prototype.handleGameAccountStatusAction = function (action) {
        var parsingResult = changeGameAccountStatusActionSchema.safeParse(action);
        if (!parsingResult.success) {
            console.error('Error parsing game account status action', parsingResult.error);
            return;
        }
        var changeData = parsingResult.data.data;
        var gameId = changeData.game_id, status = changeData.status;
        var game = this.getGame(gameId);
        if (game && status === GamesAccountStatuses.DECLINED) {
            this.deleteGame(gameId);
        }
    };
    GamesStore.prototype.handleGameTrackableMappingUpdate = function (action) {
        var parsingResult = updateGameTrackableSchema.safeParse(action);
        if (!parsingResult.success) {
            console.error('Error parsing game trackable mapping update', parsingResult.error);
            return;
        }
        var updateData = parsingResult.data.data;
        var gameId = updateData.game_id, accountId = updateData.account_id, gameProductId = updateData.game_product_id, mappedProductId = updateData.mapped_product_id;
        var game = this.getGame(gameId);
        if (!game || !game.trackables) {
            return;
        }
        var trackables = game.trackables;
        var indexOfTrackableWithMatchingGameProductId = trackables
            .findIndex(function (trackable) { return trackable.game_product_id === gameProductId; });
        if (indexOfTrackableWithMatchingGameProductId === -1) {
            return;
        }
        var originalTrackable = trackables[indexOfTrackableWithMatchingGameProductId];
        var indexOfInfoItemWithMatchingAccountId = originalTrackable.info
            .findIndex(function (info) { return info.account_id === accountId; });
        if (indexOfInfoItemWithMatchingAccountId === -1) {
            return;
        }
        var oldInfoItem = originalTrackable.info[indexOfInfoItemWithMatchingAccountId];
        var newInfoItem = __assign(__assign({}, oldInfoItem), { id: mappedProductId });
        var newTrackable = __assign(__assign({}, originalTrackable), { info: replaceElementAtIndex(originalTrackable.info, indexOfInfoItemWithMatchingAccountId, newInfoItem) });
        var newGameObject = __assign(__assign({}, game), { trackables: replaceElementAtIndex(game.trackables, indexOfTrackableWithMatchingGameProductId, newTrackable) });
        this.setGame(gameId, newGameObject);
    };
    GamesStore.prototype.getGamesList = function () {
        return Object.keys(this.data.games);
    };
    GamesStore.prototype.getAllGames = function () {
        return Object.values(this.data.games);
    };
    return GamesStore;
}(EventEmitter));
export default new GamesStore();
